import type { QrLayoutConfig, SlotLayoutConfig, SlotOrientation, SlotRatio } from './types'

/**
 * Layout-designer maths, ported from Views/CreateLayoutWindow.xaml.cs.
 *
 * The arithmetic here is deliberately integer and deliberately lossy, because
 * the WPF designer used C# `int` division and existing operator layouts were
 * authored against that behaviour. Reproducing it exactly is what lets the PWA
 * open `layouts.json` files the desktop app wrote — and write files the desktop
 * app can still open.
 */

/** Screen-only scale for the designer canvas. Never applied at composite time. */
export const PREVIEW_SCALE = 0.5

/** The designer's slot defaults (`CreateSlotGroupBox`). */
export const SLOT_DEFAULTS = {
  orientation: 'Portrait' as SlotOrientation,
  ratio: '2:3' as SlotRatio,
  x: 0,
  y: 0,
  width: 200,
  height: 300,
} as const

/** The designer's QR defaults (`QrGroupbox` in the XAML). */
export const QR_DEFAULTS = { x: 0, y: 0, width: 100, height: 100 } as const

/**
 * What the designer holds while a slot is being edited.
 *
 * `width`/`height` here are the two TEXT BOXES, not the saved geometry: the
 * width box always means "the short side of the ratio". The Landscape swap
 * happens on save, in `toSlotConfig`.
 */
export interface SlotDraft {
  orientation: SlotOrientation
  ratio: SlotRatio
  x: number
  y: number
  width: number
  height: number
}

/**
 * Derives the read-only height box from the width box.
 *
 *   2:3  ->  height = floor(width / 2) * 3
 *   1:1  ->  height = width
 *
 * The integer division is load-bearing: width 201 yields 300, not 301.5. It is
 * `(int.Parse(W) / ratioX) * ratioY` in the original, evaluated left to right in
 * C# integer arithmetic.
 */
export function deriveHeight(width: number, ratio: SlotRatio): number {
  const [rx, ry] = ratio === '1:1' ? [1, 1] : [2, 3]
  return Math.trunc(Math.trunc(width) / rx) * ry
}

/**
 * Recomputes a draft after an edit, mirroring `RenderPreview(slot, isSize)`.
 *
 * `cause` distinguishes the two call sites, because they behave differently:
 * when the RATIO (or position/orientation) changed, switching to 1:1 first
 * clamps the width down to `min(width, height)`; when the WIDTH box itself
 * changed, that clamp is skipped so typing a wider value is not fought.
 */
export function recomputeSlot(draft: SlotDraft, cause: 'width' | 'other'): SlotDraft {
  let width = Math.trunc(draft.width)

  if (draft.ratio === '1:1' && cause !== 'width') {
    const minSize = Math.min(width, Math.trunc(draft.height))
    if (minSize > 0) width = minSize
  }

  return { ...draft, width, height: deriveHeight(width, draft.ratio) }
}

/**
 * Serialises a draft into its `layouts.json` form.
 *
 * The swap is the important part: for a Landscape slot the stored `width` is
 * the UI's HEIGHT box and the stored `height` is the UI's WIDTH box, so saved
 * geometry is already in final canvas orientation and the compositor needs no
 * further interpretation. X and Y are never swapped.
 */
export function toSlotConfig(draft: SlotDraft): SlotLayoutConfig {
  const portrait = draft.orientation === 'Portrait'
  return {
    orientation: draft.orientation,
    x: Math.trunc(draft.x),
    y: Math.trunc(draft.y),
    width: portrait ? Math.trunc(draft.width) : Math.trunc(draft.height),
    height: portrait ? Math.trunc(draft.height) : Math.trunc(draft.width),
    ratio: draft.ratio,
  }
}

/** Inverse of `toSlotConfig`, for reopening a saved layout in the designer. */
export function toSlotDraft(slot: SlotLayoutConfig): SlotDraft {
  const portrait = slot.orientation === 'Portrait'
  return {
    orientation: slot.orientation,
    ratio: slot.ratio,
    x: slot.x,
    y: slot.y,
    width: portrait ? slot.width : slot.height,
    height: portrait ? slot.height : slot.width,
  }
}

/**
 * On-screen size of a slot's preview label. Portrait draws width x height;
 * Landscape draws them swapped, which is the visual counterpart of the save-time
 * swap above.
 */
export function previewBox(
  draft: SlotDraft,
  /** Screen scale. Defaults to the desktop designer's fixed 0.5. */
  scale: number = PREVIEW_SCALE,
): { left: number; top: number; width: number; height: number } {
  const rect = slotRect(draft)
  return {
    left: rect.x * scale,
    top: rect.y * scale,
    width: rect.width * scale,
    height: rect.height * scale,
  }
}

/** The QR box is forced square — the designer mirrors width into height. */
export function toQrConfig(draft: { x: number; y: number; width: number }): QrLayoutConfig {
  const size = Math.trunc(draft.width)
  return { x: Math.trunc(draft.x), y: Math.trunc(draft.y), width: size, height: size }
}

/**
 * Fits a background into the designer viewport.
 *
 * `PREVIEW_SCALE` is a fixed 0.5, which was right when the desktop designer had
 * one window size and operators authored 1620x1080 frames. A 4R background at
 * 600 DPI is 2400x3600, and half of that is 1200x1800 — taller than any laptop
 * screen and wider than the canvas column, so the slots at the top and left
 * became unreachable. The scale is a screen-only concern (§5), so it can vary;
 * nothing saved depends on it.
 *
 * Never scales ABOVE 1: a 400px-wide frame blown up to fill a 1200px column
 * would look sharp in the designer and nothing like the print.
 */
export function fitScale(
  backgroundWidth: number,
  backgroundHeight: number,
  viewWidth: number,
  viewHeight: number,
): number {
  if (backgroundWidth <= 0 || backgroundHeight <= 0) return PREVIEW_SCALE
  if (viewWidth <= 0 || viewHeight <= 0) return PREVIEW_SCALE
  return Math.min(1, viewWidth / backgroundWidth, viewHeight / backgroundHeight)
}

/** The slot's rect in BACKGROUND PIXEL SPACE, with the Landscape swap applied. */
export function slotRect(draft: SlotDraft): { x: number; y: number; width: number; height: number } {
  const portrait = draft.orientation === 'Portrait'
  return {
    x: draft.x,
    y: draft.y,
    width: portrait ? draft.width : draft.height,
    height: portrait ? draft.height : draft.width,
  }
}

/**
 * Whether a slot lies entirely on the background.
 *
 * The compositor draws wherever it is told, so a slot pushed off the edge is
 * simply invisible in the print — with nothing on screen to say so, because the
 * designer canvas clips it too. Worth surfacing rather than discovering on
 * paper.
 */
export function isSlotWithin(
  draft: SlotDraft,
  backgroundWidth: number,
  backgroundHeight: number,
): boolean {
  const rect = slotRect(draft)
  return (
    rect.x >= 0 &&
    rect.y >= 0 &&
    rect.x + rect.width <= backgroundWidth &&
    rect.y + rect.height <= backgroundHeight
  )
}

/** Same test for the QR box, which is always square. */
export function isQrWithin(
  qr: { x: number; y: number; width: number },
  backgroundWidth: number,
  backgroundHeight: number,
): boolean {
  return (
    qr.x >= 0 &&
    qr.y >= 0 &&
    qr.x + qr.width <= backgroundWidth &&
    qr.y + qr.width <= backgroundHeight
  )
}

/** Moves a slot, clamping it to stay on the background. Used by canvas dragging. */
export function moveSlot(
  draft: SlotDraft,
  x: number,
  y: number,
  backgroundWidth: number,
  backgroundHeight: number,
): SlotDraft {
  const rect = slotRect(draft)
  return {
    ...draft,
    x: Math.max(0, Math.min(Math.round(x), Math.max(0, backgroundWidth - rect.width))),
    y: Math.max(0, Math.min(Math.round(y), Math.max(0, backgroundHeight - rect.height))),
  }
}

/**
 * Smallest slot the designer will let you drag to.
 *
 * Not zero: a slot dragged to nothing is invisible on the canvas and impossible
 * to grab again, and the number boxes are the way to type an unusual value.
 */
export const MIN_SLOT_WIDTH = 20

/**
 * The width box that would derive approximately `height`.
 *
 * `deriveHeight` is deliberately lossy — `floor(width/2)*3` for 2:3 — so this is
 * an inverse only up to that truncation, which is exactly what a drag needs: it
 * proposes a width, and `recomputeSlot` then applies the real maths.
 */
export function widthForDerivedHeight(height: number, ratio: SlotRatio): number {
  if (ratio === '1:1') return Math.round(height)
  return Math.round((Math.max(0, height) * 2) / 3)
}

/**
 * Resizes a slot from a dragged corner.
 *
 * `rectWidth`/`rectHeight` are the rect the pointer is asking for, in
 * BACKGROUND pixels and in canvas orientation — so for a Landscape slot they
 * are already the swapped pair. Only the width box can actually be set (height
 * is derived from the ratio), so both axes are converted into candidate widths
 * and the larger wins: the slot grows to cover the pointer, which is how an
 * aspect-locked handle is expected to behave.
 *
 * The result is clamped so the slot cannot be dragged off the background — the
 * same rule `moveSlot` applies, since a slot past the edge is silently dropped
 * from the print.
 */
export function resizeSlot(
  draft: SlotDraft,
  rectWidth: number,
  rectHeight: number,
  backgroundWidth: number,
  backgroundHeight: number,
): SlotDraft {
  const portrait = draft.orientation === 'Portrait'

  // Which asked-for axis is the width box, and which is the derived one.
  const fromWidthBox = portrait ? rectWidth : rectHeight
  const fromDerived = portrait ? rectHeight : rectWidth

  let width = Math.max(fromWidthBox, widthForDerivedHeight(fromDerived, draft.ratio))

  // Room left on the background, expressed as a width-box cap on each axis.
  const roomX = Math.max(0, backgroundWidth - draft.x)
  const roomY = Math.max(0, backgroundHeight - draft.y)
  const capFromWidthBox = portrait ? roomX : roomY
  const capFromDerived = widthForDerivedHeight(portrait ? roomY : roomX, draft.ratio)

  width = Math.min(width, capFromWidthBox, capFromDerived)
  width = Math.max(MIN_SLOT_WIDTH, Math.round(width))

  // `'width'` is the cause the desktop designer used when the width box itself
  // was edited: it skips the 1:1 clamp, so dragging wider is not fought.
  return recomputeSlot({ ...draft, width }, 'width')
}

/** Resizes the QR box, which is square and needs no ratio inversion. */
export function resizeQr(
  qr: { x: number; y: number; width: number },
  rectWidth: number,
  rectHeight: number,
  backgroundWidth: number,
  backgroundHeight: number,
): { x: number; y: number; width: number } {
  const asked = Math.max(rectWidth, rectHeight)
  const room = Math.min(backgroundWidth - qr.x, backgroundHeight - qr.y)
  return { ...qr, width: Math.max(MIN_SLOT_WIDTH, Math.round(Math.min(asked, room))) }
}

/**
 * Keyboard nudge distance, in BACKGROUND pixels.
 *
 * Background pixels, not screen pixels, because that is the unit everything
 * else in the designer speaks — a nudge and the number box agree, whatever the
 * canvas is scaled to. At a 19% fit one step is a sub-pixel move on screen and
 * the number box is what visibly changes; that is the point of the fine step.
 *
 * 1 and 10 is the convention every design tool uses, so it needs no explaining.
 */
export const NUDGE_FINE = 1
export const NUDGE_COARSE = 10

/**
 * Maps an arrow key to a nudge, or null when the key is not one.
 *
 * Returning null rather than a zero vector matters: the caller uses it to
 * decide whether to call `preventDefault`, and swallowing every keystroke on a
 * focused slot would trap Tab.
 */
export function nudgeForKey(
  key: string,
  shiftKey = false,
): { dx: number; dy: number } | null {
  const step = shiftKey ? NUDGE_COARSE : NUDGE_FINE
  switch (key) {
    case 'ArrowLeft':
      return { dx: -step, dy: 0 }
    case 'ArrowRight':
      return { dx: step, dy: 0 }
    case 'ArrowUp':
      return { dx: 0, dy: -step }
    case 'ArrowDown':
      return { dx: 0, dy: step }
    default:
      return null
  }
}

/**
 * How near a drag has to come before it snaps, in SCREEN pixels.
 *
 * Screen rather than background pixels because it is a gesture tolerance, not a
 * geometry: it has to feel the same whether the canvas is fitted at 19% or 100%.
 * `snapTolerance` converts it for the caller.
 */
export const SNAP_SCREEN_PX = 7

/** The screen tolerance in background pixels, for the canvas's current scale. */
export function snapTolerance(scale: number): number {
  return scale > 0 ? SNAP_SCREEN_PX / scale : SNAP_SCREEN_PX
}

export interface SnapResult {
  x: number
  y: number
  /** The box was pulled onto the background's vertical centre line. */
  centredX: boolean
  /** The box was pulled onto the background's horizontal centre line. */
  centredY: boolean
}

/**
 * Pulls a dragged box onto the background's centre lines.
 *
 * Centring a slot by eye is the one placement a drag genuinely cannot do — the
 * number boxes could, but only by working out `(2400 - 900) / 2` first, which is
 * the arithmetic this screen exists to avoid.
 *
 * Each axis snaps independently, so a box can sit centred horizontally while
 * being dragged freely up and down. The flags come back so the caller can draw
 * the guide that explains why the box stopped following the cursor.
 *
 * Deliberately NOT applied to arrow-key nudges: those exist to place a slot on
 * an exact pixel, and a snap that swallowed the last few presses would fight
 * the one gesture that is supposed to be precise.
 */
export function snapToCentre(
  x: number,
  y: number,
  rectWidth: number,
  rectHeight: number,
  backgroundWidth: number,
  backgroundHeight: number,
  tolerance: number,
): SnapResult {
  const centreX = (backgroundWidth - rectWidth) / 2
  const centreY = (backgroundHeight - rectHeight) / 2

  const centredX = Math.abs(x - centreX) <= tolerance
  const centredY = Math.abs(y - centreY) <= tolerance

  return {
    x: centredX ? Math.round(centreX) : x,
    y: centredY ? Math.round(centreY) : y,
    centredX,
    centredY,
  }
}
